import { AlertCircle } from 'lucide-react-native';
import { Component, ReactNode } from 'react';

import { Card } from '@/components/ui/card';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { Sentry } from '@/util/sentry';

type DirectoryCardErrorBoundaryProps = {
  children: ReactNode;
  expertId?: string | number;
};

type DirectoryCardErrorBoundaryState = {
  hasError: boolean;
};

export class DirectoryCardErrorBoundary extends Component<
  DirectoryCardErrorBoundaryProps,
  DirectoryCardErrorBoundaryState
> {
  constructor(props: DirectoryCardErrorBoundaryProps) {
    super(props);

    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): DirectoryCardErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    Sentry.captureException(error, {
      tags: {
        component: 'DirectoryCardErrorBoundary',
        boundaryType: 'directory-card',
      },
      extra: {
        errorMessage: error?.message,
        errorName: error?.name,
        componentStack: errorInfo?.componentStack,
        expertId: this.props.expertId,
      },
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <Card variant="outline" className="flex-1 p-4">
          <VStack className="items-center justify-center py-8">
            <HStack space="md" className="items-center">
              <Icon as={AlertCircle} size="sm" className="text-typography-400" />
              <Text size="sm" className="text-typography-500">
                Unable to load this expert
              </Text>
            </HStack>
          </VStack>
        </Card>
      );
    }

    return this.props.children;
  }
}
